import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import './App.css';
import SignUpModal from './components/SignUpModal';
import PrivacyPolicy from './PrivacyPolicy';
import VendorAssessment from './VendorAssessment';
import { initGA, trackPageView, trackNavigationClick } from './utils/analytics';

function App() {
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize Google Analytics on app mount
  useEffect(() => {
    initGA();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  // Auto-open modal when navigating to /signup
  useEffect(() => {
    if (location.pathname === '/signup') {
      setShowSignUpModal(true);
    }
  }, [location.pathname]);

  // Handle modal close - navigate back to home if on /signup route
  const handleModalClose = () => {
    setShowSignUpModal(false);
    if (location.pathname === '/signup') {
      navigate('/');
    }
  };

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false); // Close mobile menu when navigating
    if (sectionId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Home page content (used for both / and /signup routes)
  const homePageContent = (
    <>
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo" onClick={() => scrollToSection('home')}>
            <span className="logo-trust">Trust</span>
            <span className="logo-dot">·</span>
            <span className="logo-powered">Powered</span>
            <span className="logo-dot">·</span>
            <span className="logo-ai">AI</span>
          </div>

          {/* Hamburger Menu Button (Mobile Only) */}
          <button
            className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Desktop Navigation */}
          <nav className="nav-menu desktop-menu">
            <button className="nav-item" onClick={() => {
              trackNavigationClick('Home');
              scrollToSection('home');
            }}>
              Home
            </button>
            <button className="nav-item" onClick={() => {
              trackNavigationClick('Challenge');
              scrollToSection('challenge');
            }}>
              Challenge
            </button>
            <button className="nav-item" onClick={() => {
              trackNavigationClick('Philosophy');
              scrollToSection('philosophy');
            }}>
              Philosophy
            </button>
            <button className="nav-item" onClick={() => {
              trackNavigationClick('Framework');
              scrollToSection('framework');
            }}>
              Framework
            </button>
            <button className="nav-item signup-button" onClick={() => {
              trackNavigationClick('Sign Up Button');
              setShowSignUpModal(true);
            }}>
              Sign Up
            </button>
          </nav>

          {/* Mobile Navigation Menu */}
          <nav className={`nav-menu mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <button className="nav-item" onClick={() => {
              trackNavigationClick('Home');
              scrollToSection('home');
            }}>
              Home
            </button>
            <button className="nav-item" onClick={() => {
              trackNavigationClick('Challenge');
              scrollToSection('challenge');
            }}>
              Challenge
            </button>
            <button className="nav-item" onClick={() => {
              trackNavigationClick('Philosophy');
              scrollToSection('philosophy');
            }}>
              Philosophy
            </button>
            <button className="nav-item" onClick={() => {
              trackNavigationClick('Framework');
              scrollToSection('framework');
            }}>
              Framework
            </button>
            <button className="nav-item signup-button" onClick={() => {
              trackNavigationClick('Sign Up Button');
              setShowSignUpModal(true);
              setMobileMenuOpen(false);
            }}>
              Sign Up
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-background">
          <div className="floating-shape shape-1"></div>
          <div className="floating-shape shape-2"></div>
          <div className="floating-shape shape-3"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge">🚀 AI Compliance, Reimagined</div>
          <h1 className="hero-title">Compliance Accelerates AI Innovation</h1>
          <p className="hero-subtitle">The counter-intuitive truth about AI compliance</p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-icon">⚡</span>
              <span className="stat-text">Faster Approvals</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🛡️</span>
              <span className="stat-text">Enhanced Security</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">✓</span>
              <span className="stat-text">Full Compliance</span>
            </div>
          </div>
          <div className="hero-cta">
            <Link
              to="/vendor-assessment"
              className="hero-cta-button"
              onClick={() => trackNavigationClick('Hero CTA - Vendor Assessment')}
            >
              Try the framework: Get a free AI vendor risk assessment →
            </Link>
            <p className="hero-cta-subtext">24-hour turnaround · One vendor · No card required</p>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section id="challenge" className="section problem-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">The Challenge</span>
            <h2 className="section-title">What's Holding Back AI Adoption?</h2>
          </div>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="card-icon">👥</div>
              <h3>Shadow AI</h3>
              <p>Employees independently adopt AI tools without organizational visibility, creating security and compliance risks.</p>
              <div className="card-accent"></div>
            </div>
            <div className="problem-card">
              <div className="card-icon">📋</div>
              <h3>Fragmented Requirements</h3>
              <p>Compliance requirements scattered across multiple frameworks, forcing manual interpretation and increasing effort.</p>
              <div className="card-accent"></div>
            </div>
            <div className="problem-card">
              <div className="card-icon">⏱️</div>
              <h3>Slow Approvals</h3>
              <p>AI tool implementation drags on for months, losing momentum during security reviews and approval processes.</p>
              <div className="card-accent"></div>
            </div>
            <div className="problem-card">
              <div className="card-icon">🔓</div>
              <h3>Security Risks</h3>
              <p>Data leakage concerns and security vulnerabilities slow down or completely halt AI adoption initiatives.</p>
              <div className="card-accent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* The Philosophy Section */}
      <section id="philosophy" className="section philosophy-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Philosophy</span>
            <h2 className="section-title">A Different Approach</h2>
          </div>
          <div className="philosophy-content">
            <div className="philosophy-visual">
              <div className="philosophy-box old-way">
                <div className="box-label">❌ Old Way</div>
                <div className="box-content">Compliance as Blocker</div>
              </div>
              <div className="arrow-transform">→</div>
              <div className="philosophy-box new-way">
                <div className="box-label">✅ New Way</div>
                <div className="box-content">Compliance as Enabler</div>
              </div>
            </div>
            <h3 className="philosophy-headline">Compliance Done Right = Speed, Not Slowdown</h3>
            <p className="philosophy-text">
              Most organizations view compliance as a barrier to innovation-a necessary evil that slows down AI adoption.
              This mindset creates friction between security teams and engineers, leading to shadow AI usage, delayed projects,
              and missed opportunities.
            </p>
            <p className="philosophy-text">
              But there's a better way. When compliance is built into your AI strategy from the start, it becomes an enabler
              of speed and confidence. Clear guardrails allow teams to move faster, not slower. Proper frameworks eliminate
              uncertainty and reduce the back-and-forth that actually causes delays.
            </p>
            <div className="philosophy-highlight">
              <span className="highlight-icon">💡</span>
              <span>The paradigm shift: from compliance as blocker → compliance as enabler</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Compliance Matters Section */}
      <section className="section why-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Impact</span>
            <h2 className="section-title">Why AI Compliance Matters</h2>
          </div>
          <div className="why-content">
            <div className="why-block impact-block">
              <div className="block-header">
                <span className="block-icon">💼</span>
                <h3>Business Impact</h3>
              </div>
              <p>Without proper AI compliance, organizations face:</p>
              <ul>
                <li>Lost business opportunities with compliance-sensitive buyers</li>
                <li>Security breaches and data leakage incidents</li>
                <li>Reputation damage from AI misuse or failures</li>
                <li>Legal and regulatory penalties</li>
                <li>Reduced team productivity from uncertainty and fear</li>
              </ul>
            </div>
            <div className="why-block regulatory-block">
              <div className="block-header">
                <span className="block-icon">📜</span>
                <h3>Regulatory Landscape</h3>
              </div>
              <p>AI implementations must navigate an increasingly complex regulatory environment:</p>
              <div className="framework-badges">
                <span className="framework-badge gdpr">GDPR</span>
                <span className="framework-badge soc2">SOC 2 Type II</span>
                <span className="framework-badge iso">ISO/IEC 42001</span>
                <span className="framework-badge euai">EU AI Act</span>
                <span className="framework-badge hipaa">HIPAA</span>
                <span className="framework-badge pci">PCI DSS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Framework Section */}
      <section id="framework" className="section framework-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Framework</span>
            <h2 className="section-title">The Three-Pillar Framework</h2>
          </div>
          <p className="framework-intro">
            A comprehensive approach to AI compliance that enables speed and innovation
          </p>
          <div className="pillars-grid">
            <div className="pillar-card pillar-1">
              <div className="pillar-number">1</div>
              <div className="pillar-icon-large">🔎</div>
              <h3>Monitoring</h3>
              <p>
                Gain complete visibility into AI usage across your organization. Understand what tools are being used,
                by whom, and how. Real-time monitoring ensures you can identify risks before they become problems.
              </p>
              <div className="pillar-features">
                <span className="feature-tag">Real-time Tracking</span>
                <span className="feature-tag">Usage Analytics</span>
                <span className="feature-tag">Risk Detection</span>
              </div>
            </div>
            <div className="pillar-card pillar-2">
              <div className="pillar-number">2</div>
              <div className="pillar-icon-large">🛡️</div>
              <h3>Control</h3>
              <p>
                Implement technical guardrails and clear policies that protect your organization without blocking innovation.
                Access controls, data masking, logging, and automated compliance checks built into your workflows.
              </p>
              <div className="pillar-features">
                <span className="feature-tag">Access Controls</span>
                <span className="feature-tag">Policy Enforcement</span>
                <span className="feature-tag">Automated Checks</span>
              </div>
              <Link
                to="/vendor-assessment"
                className="pillar-cta-link"
                onClick={() => trackNavigationClick('Control Pillar - Vendor Assessment')}
              >
                → See Control in action: free vendor risk assessment
              </Link>
            </div>
            <div className="pillar-card pillar-3">
              <div className="pillar-number">3</div>
              <div className="pillar-icon-large">🎓</div>
              <h3>Education/Training</h3>
              <p>
                Enable your teams with clear guidance on AI usage, limitations, and best practices. Well-trained employees
                become your first line of defense and your strongest advocates for responsible AI adoption.
              </p>
              <div className="pillar-features">
                <span className="feature-tag">Team Training</span>
                <span className="feature-tag">Best Practices</span>
                <span className="feature-tag">Continuous Learning</span>
              </div>
            </div>
          </div>
          <div className="framework-summary">
            <div className="summary-icon">🎯</div>
            <p>
              These three pillars work together to create an environment where teams can confidently adopt AI tools,
              knowing they have the visibility, controls, and knowledge to do so safely and compliantly.
            </p>
          </div>
        </div>
      </section>

      {/* Closing CTA Section */}
      <section className="section closing-cta-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">🚀 Start Here</span>
            <h2 className="section-title">Experience the framework in 24 hours.</h2>
            <p className="closing-cta-subhead">
              Talking about compliance is easy. Producing a defensible vendor risk assessment is the test.
            </p>
          </div>
          <div className="closing-cta-card">
            <p className="closing-cta-text">
              Submit one AI vendor your team is evaluating right now. We'll deliver a full risk
              assessment within 24 hours.
            </p>
            <p className="closing-cta-microcopy">No card. No call required. One vendor per company.</p>
            <Link
              to="/vendor-assessment"
              className="closing-cta-button"
              onClick={() => trackNavigationClick('Closing CTA - Vendor Assessment')}
            >
              Request your free snapshot →
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2026 TrustPoweredAI. All rights reserved.</p>
      </footer>
    </>
  );

  return (
    <div className="App">
      <Routes>
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/vendor-assessment" element={<VendorAssessment />} />
        <Route path="/" element={homePageContent} />
        <Route path="/signup" element={homePageContent} />
      </Routes>

      <SignUpModal
        isOpen={showSignUpModal}
        onClose={handleModalClose}
      />
    </div>
  );
}

export default App;
